import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  author?: string;
  section?: string;
}

const BASE_URL = 'https://lqpglobal.com';
const DEFAULT_IMAGE = 'https://lqpglobal.com/og-image.jpg';
const SITE_NAME = 'LQP Global';

const updateMetaTag = (property: string, content: string, isProperty = false) => {
  const attribute = isProperty ? 'property' : 'name';
  let element = document.querySelector(`meta[${attribute}="${property}"]`) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, property);
    document.head.appendChild(element);
  }

  element.setAttribute('content', content);
};

const updateLinkTag = (rel: string, href: string) => {
  let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const SEO: React.FC<SEOProps> = ({
  title = 'LQP Global | Ordenamos la complejidad digital de tu negocio',
  description = 'Soluciones digitales premium para pymes: sistemas a medida, automatización, inteligencia artificial y e-commerce. Transformamos procesos desordenados en negocios escalables.',
  keywords = 'software para pymes, desarrollo web empresas, automatización empresarial, inteligencia artificial pymes, sistemas de gestión, digitalización pymes, e-commerce Argentina, desarrollo a medida',
  image = DEFAULT_IMAGE,
  url = BASE_URL,
  type = 'website',
  publishedTime,
  author = 'LQP Global',
  section
}) => {
  const fullTitle = title.includes('LQP') ? title : `${title} | LQP Global`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Basic Meta Tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

    // Canonical URL
    updateLinkTag('canonical', url);

    // Open Graph / Facebook
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:site_name', SITE_NAME, true);
    updateMetaTag('og:locale', 'es_AR', true);

    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', url);
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Article specific (for blog posts)
    if (type === 'article' && publishedTime) {
      updateMetaTag('article:published_time', publishedTime, true);
    }
    if (type === 'article' && author) {
      updateMetaTag('article:author', author, true);
    }
    if (type === 'article' && section) {
      updateMetaTag('article:section', section, true);
    }

    // Additional SEO Tags
    updateMetaTag('theme-color', '#050505');
    updateMetaTag('geo.region', 'AR');
    updateMetaTag('geo.placename', 'Argentina');

  }, [fullTitle, description, keywords, image, url, type, publishedTime, author, section]);

  return null;
};

export default SEO;
